import { ScheduledSetting as ShowSetting, ScheduleMarker, ScheduleDraftStatus, useScheduledSettings } from './Scheduling/ScheduledSettings';
import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import React from 'react';

import Typography from '@material-ui/core/Typography';

import { ShowSettingMinMax } from '../FormWidgets/ShowSliderSetting';
import {
  EnableDayTimeDisableSelect,
  RoomScalseSelect,
  CylinderValveSelect,
  CylinderStatusSelect
} from '../FormWidgets/Select.js';
import Desc from '../FormWidgets/Description';
import { SettingGroupGrid, ValueBox } from '../FormWidgets/SettingGroup';
import { room_scale_mapping } from '../Constants/constants';
import { LoadSettingsDialog } from '../Utils/load_settings';

import { useCo2Settings } from '../Hooks/useCo2Settings';
import {
  co2SettingsForSubmission,
  supportsCo2Cylinders,
  supportsCo2SettingsRelocation
} from '../Utils/co2_utils';

import { SettingActions } from '../components';

export default function Co2Settings({ ...other }) {
  const {
    t,
    params,
    stateManager,
    controller,
    parent,
    controllerSnapshot,
    parentSnapshot,
    values: ordinaryValues,
    setValues,
    controls: ordinaryControls,
    openLoadSettingsDialog,
    closeLoadSettingsDialog,
    tab_id
  } = useCo2Settings();
  const schedule = useScheduledSettings(controller, parent, 'co2', ordinaryValues, ordinaryControls);
  const { values, controls } = schedule;
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  const saveSettings = controls.saveSettings;
  controls.saveSettings = () =>
    saveSettings((settings) => co2SettingsForSubmission(controller, settings));

  const relocated = supportsCo2SettingsRelocation(controllerSnapshot);
  const read_access_advanced = controllerSnapshot.access_rights.advanced.read;
  const read_access_ventilation = controllerSnapshot.access_rights.ventilation.read;
  const write_access = controllerSnapshot.access_rights.co2.write;

  return (
    <React.Fragment>
      <ScheduleDraftStatus schedule={schedule} />
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={ordinaryControls}
      />

      {schedule.canSave && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={schedule?.saving || connectionStatus !== 'good'}
          onSave={controls.saveSettings}
          onReset={controls.resetSettings}
          disabled={!write_access}
        />
      )}

      <SettingGroupGrid controller={controllerSnapshot} id={'co2_settings'}>
        <ShowSetting
          schedule={schedule}
          id="co2_sp"
          controller={controllerSnapshot}
          cur_value={values.co2_sp}
          controller_value={parentSnapshot.settings.co2.co2_sp}
          controls={controls}
          disabled={schedule.disabled('co2_sp')}
        />
        <ShowSetting
          schedule={schedule}
          id="room_vol_sp"
          controller={controllerSnapshot}
          cur_value={values.room_vol_sp}
          controller_value={parentSnapshot.settings.co2.room_vol_sp}
          controls={controls}
          max={room_scale_mapping['room_vol_sp'][values.room_scale]}
          type="float"
          disabled={schedule.disabled('room_vol_sp')}
        />
        <ShowSetting
          schedule={schedule}
          id="co2_flow"
          controller={controllerSnapshot}
          cur_value={values.co2_flow}
          controller_value={parentSnapshot.settings.co2.co2_flow}
          controls={controls}
          max={room_scale_mapping['co2_flow'][values.room_scale]}
          type="float"
          disabled={schedule.disabled('co2_flow')}
        />
        <ShowSetting
          schedule={schedule}
          id="exhaust_fans_flow"
          controller={controllerSnapshot}
          cur_value={values.exhaust_fans_flow}
          controller_value={parentSnapshot.settings.co2.exhaust_fans_flow}
          max={room_scale_mapping['exhaust_fans_flow'][values.room_scale]}
          controls={controls}
          disabled={schedule.disabled('exhaust_fans_flow')}
        />
        <ValueBox controller={controllerSnapshot} id={'co2_control'}>
          <Typography>
            <ScheduleMarker target={schedule('co2_control')} />
            <Desc id="co2_control" />
          </Typography>
          <EnableDayTimeDisableSelect controls={controls}
            disabled={schedule.disabled('co2_control')}
            value={values.co2_control}
            controller_value={parentSnapshot.settings.co2.co2_control}
            name="co2_control"
            onChange={(event, value) => controls.setValue('co2_control', event.target.value)}
          />
        </ValueBox>
        <ValueBox controller={controllerSnapshot} id={'co2_room_scale'}>
          <Typography>
            <Desc id="room_scale" />
          </Typography>
          <RoomScalseSelect controls={controls}
            value={values.room_scale}
            controller_value={parentSnapshot.settings.co2.room_scale}
            name="room_scale"
            onChange={(event, value) => controls.setValue('room_scale', event.target.value)}
          />
        </ValueBox>
      </SettingGroupGrid>

      {supportsCo2Cylinders(controllerSnapshot) && (
        <SettingGroupGrid controller={controllerSnapshot} id={'co2_more'}>
          <ValueBox controller={controllerSnapshot} id={'co2_cylinder_current_valve'}>
            <Typography>
              <Desc id="co2_cylinder_current_valve" />
            </Typography>
            <CylinderValveSelect
              disabled={!write_access}
              inputProps={{ 'aria-label': t('co2_cylinder_current_valve') }}
              controls={controls}
              value={values.co2_cylinder_current_valve}
              controller_value={parentSnapshot.settings.co2.co2_cylinder_current_valve}
              name="co2_cylinder_current_valve"
              onChange={(event, value) =>
                controls.setValue('co2_cylinder_current_valve', event.target.value)
              }
            />
          </ValueBox>
          <></>
          <ValueBox controller={controllerSnapshot} id={'co2_cylinder_1_status'}>
            <Typography>
              <Desc id="co2_cylinder_1_status" />
            </Typography>
            <CylinderStatusSelect
              disabled={!write_access}
              inputProps={{ 'aria-label': t('co2_cylinder_1_status') }}
              controls={controls}
              value={values.co2_cylinder_1_status}
              controller_value={parentSnapshot.settings.co2.co2_cylinder_1_status}
              name="co2_cylinder_1_status"
              onChange={(event, value) =>
                controls.setValue('co2_cylinder_1_status', event.target.value)
              }
            />
          </ValueBox>
          <ValueBox controller={controllerSnapshot} id={'co2_cylinder_2_status'}>
            <Typography>
              <Desc id="co2_cylinder_2_status" />
            </Typography>
            <CylinderStatusSelect
              disabled={!write_access}
              inputProps={{ 'aria-label': t('co2_cylinder_2_status') }}
              controls={controls}
              value={values.co2_cylinder_2_status}
              controller_value={parentSnapshot.settings.co2.co2_cylinder_2_status}
              name="co2_cylinder_2_status"
              onChange={(event, value) =>
                controls.setValue('co2_cylinder_2_status', event.target.value)
              }
            />
          </ValueBox>
        </SettingGroupGrid>
      )}

      {!relocated && (
      <SettingGroupGrid controller={controllerSnapshot} id={'temperature_co2'}>
        <ShowSetting
          schedule={schedule}
          id="temp_sp_co2"
          controller={controllerSnapshot}
          cur_value={values.temp_sp_co2}
          controller_value={parentSnapshot.settings.co2.temp_sp_co2}
          controls={controls}
          disabled={schedule.disabled('temp_sp_co2')}
          type="float"
        />

        { /* Changed Version-Guard from ADE -> ADED since older controls where incorrectly shiped with ADED. */ }
        {controllerSnapshot.api_version <= 'ADED' && (
          <ShowSetting
            schedule={schedule}
            id="temp_heater_co2"
            controller={controllerSnapshot}
            cur_value={values.temp_heater_co2}
            controller_value={parentSnapshot.settings.co2.temp_heater_co2}
            controls={controls}
            disabled={schedule.disabled('temp_heater_co2')}
            max={Number.parseFloat((values.temp_sp_co2 - 10) / 10)}
            type="float"
            off_at={49}
          />
        )}
      </SettingGroupGrid>
      )}

      {!relocated && (
      <SettingGroupGrid controller={controllerSnapshot} id={'ventilation_co2'}>
        {[-1, 0, 1].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
          <ShowSettingMinMax
            id="exhaust_fan_ec_max_co2"
            show="exhaust_fans_ec_min_max_co2"
            controller={controllerSnapshot}
            cur_value_max={values.exhaust_fan_ec_max_co2}
            controller_value_max={parentSnapshot.settings.co2.exhaust_fan_ec_max_co2}
            cur_value_min={values.exhaust_fan_ec_min_co2}
            controller_value_min={parentSnapshot.settings.co2.exhaust_fan_ec_min_co2}
            min={9}
            max={
              read_access_ventilation && parentSnapshot.settings.ventilation.pressure_percent_day - 127 < 0
                ? 100 + (parentSnapshot.settings.ventilation.pressure_percent_day - 127)
                : 100
            }
            controls={controls}
            disabled={!write_access}
            type="fan_min_max"
          />
        )}
        {[-1, 0, 1].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
          <ShowSettingMinMax
            id="intake_fan_ec_max_co2"
            show="intake_fan_ec_min_max_co2"
            controller={controllerSnapshot}
            cur_value_max={values.intake_fan_ec_max_co2}
            controller_value_max={parentSnapshot.settings.co2.intake_fan_ec_max_co2}
            cur_value_min={values.intake_fan_ec_min_co2}
            controller_value_min={parentSnapshot.settings.co2.intake_fan_ec_min_co2}
            min={9}
            max={100}
            controls={controls}
            disabled={!write_access}
            type="fan_min_max"
          />
        )}
        {[-1, 0, 2].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
          <ShowSettingMinMax
            id="exhaust_fan_ac_max_co2"
            show="exhaust_fans_ac_min_max_co2"
            controller={controllerSnapshot}
            cur_value_max={values.exhaust_fan_ac_max_co2}
            controller_value_max={parentSnapshot.settings.co2.exhaust_fan_ac_max_co2}
            cur_value_min={values.exhaust_fan_ac_min_co2}
            controller_value_min={parentSnapshot.settings.co2.exhaust_fan_ac_min_co2}
            min={read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_exhaust_turnoff - 1 : 0}
            max={
              read_access_ventilation.read &&
              parentSnapshot.settings.ventilation.pressure_percent_day - 127 < 0
                ? 100 + (parentSnapshot.settings.ventilation.pressure_percent_day - 127)
                : 100
            }
            off_at={read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_exhaust_turnoff - 1 : 0}
            controls={controls}
            disabled={!write_access}
            type="fan_min_max"
          />
        )}
        {[-1, 0, 2].indexOf(controllerSnapshot.settings.system.fan_type_select) > -1 && (
          <ShowSettingMinMax
            id="intake_fan_ac_max_co2"
            show="intake_fan_ac_min_max_co2"
            controller={controllerSnapshot}
            cur_value_max={values.intake_fan_ac_max_co2}
            controller_value_max={parentSnapshot.settings.co2.intake_fan_ac_max_co2}
            cur_value_min={values.intake_fan_ac_min_co2}
            controller_value_min={parentSnapshot.settings.co2.intake_fan_ac_min_co2}
            min={read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_intake_turnoff - 1 : 0}
            max={100}
            off_at={read_access_advanced ? parentSnapshot.settings.advanced.fan_ac_intake_turnoff - 1 : 0}
            controls={controls}
            disabled={!write_access}
            type="fan_min_max"
          />
        )}
      </SettingGroupGrid>
      )}

      {schedule.canSave && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={schedule?.saving || connectionStatus !== 'good'}
          onSave={controls.saveSettings}
          onReset={controls.resetSettings}
          disabled={!write_access}
        />
      )}
    </React.Fragment>
  );
}
